import React from 'react';
import { Box, Button, Card, Group, Text, ThemeIcon } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import Tabla from './tabla';
import type { ITablaEditable } from './useEdicionFilas';

interface TarjetaTablaEditableProps<T extends { Id: string }> {
    titulo: string;
    icono: React.ReactNode;
    /** Aclaración corta bajo la tabla (de dónde sale o cómo se usa). */
    nota?: string;
    /** Contenido al pie de la tarjeta (p. ej. el total de la sección). */
    pie?: React.ReactNode;
    tabla: ITablaEditable<T>;
    columnas: ColumnDef<T>[];
    minWidth?: number | string;
}

/**
 * Tarjeta con una tabla de edición en línea y su botón "Agregar Fila".
 * Ocupa el alto de su contenedor: si las filas no caben, la tabla hace scroll por dentro.
 */
export const TarjetaTablaEditable = <T extends { Id: string }>({
    titulo,
    icono,
    nota,
    pie,
    tabla: { datos, meta, agregarFila },
    columnas,
    minWidth = '100%'
}: TarjetaTablaEditableProps<T>) => {
    const tabla = useReactTable({
        data: datos,
        columns: columnas,
        getCoreRowModel: getCoreRowModel(),
        getRowId: fila => fila.Id,
        meta
    });

    return (
        <Card shadow="sm" p="md" radius="md" withBorder h="100%" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Group justify="space-between" mb="xs" style={{ flexShrink: 0 }}>
                <Group gap="xs">
                    <ThemeIcon variant="light" color="primary" radius="xl" size="md">{icono}</ThemeIcon>
                    <Text fw={600} size="md">{titulo}</Text>
                </Group>
                {agregarFila && (
                    <Button color="primary" variant="light" size="xs" radius="lg" leftSection={<IconPlus color="var(--mantine-color-primary-0)" size={12} />} onClick={agregarFila} disabled={meta.filaEnEdicion !== null}>
                        <Text fw={500} size="xs">Agregar Fila</Text>
                    </Button>
                )}
            </Group>
            <Box style={{ flex: 1, minHeight: 0 }}>
                <Tabla tabla={tabla} minWidth={minWidth} modoEdicion={meta.filaEnEdicion !== null} filaEnEdicion={meta.filaEnEdicion} />
            </Box>
            {datos.length === 0 && <Text size="xs" c="dimmed" ta="center" mt="xs">Sin registros.</Text>}
            {pie && <Box mt="xs" style={{ flexShrink: 0 }}>{pie}</Box>}
            {nota && <Text size="xs" c="dimmed" mt={4} style={{ flexShrink: 0 }}>{nota}</Text>}
        </Card>
    );
};
